import { useCallback, useMemo, useState } from 'react';
import { useAppData, useAppResource } from '../../context/AppDataContext';
import { organizationService } from '../../services/organizationService';
import { userService } from '../../services/userService';
import ClientCards from '../ClientsHub/ClientCards';
import './Organizations.css';
import { PageLoader } from '../../components/Loader';
import BulkImportPanel from '../../components/BulkImportPanel';
import { toast } from '../../utils/toast';
import { StatCards } from '../../components/ScheduleKpis';
import { auditService } from '../../services/auditService';
import { outletService } from '../../services/outletService';
import { organizationKpis } from '../../utils/listKpis';

const ORG_COLUMNS = [
  { key: 'name', label: 'Organization name', required: true, aliases: ['organization', 'organisation', 'organisation name', 'name', 'client', 'client name', 'company', 'company name', 'brand'] },
  { key: 'brand_code', label: 'Brand code', hint: 'two letters, e.g. PT (optional)', aliases: ['brand code', 'code', 'short code', 'prefix'] },
  { key: 'email', label: 'Email', aliases: ['email id', 'mail', 'e-mail'] },
  { key: 'phone', label: 'Phone', aliases: ['phone number', 'mobile', 'contact', 'contact number'] },
  { key: 'address', label: 'Address', aliases: ['head office', 'office address'] },
];
const ORG_SAMPLE = [
  { name: 'Pantaloons', brand_code: 'PT', email: 'audit@pantaloons.example', phone: '9800000001', address: 'Mumbai' },
  { name: 'Croma', brand_code: 'CR', email: '', phone: '', address: '' },
];

function checkOrganizationRow(row) {
  if (!row.name) return 'Organization name is missing';
  if (row.brand_code && !/^[A-Za-z]{2}$/.test(row.brand_code)) return 'Brand code must be two letters';
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) return 'Email is not valid';
  if (row.phone && row.phone.length > 20) return 'Phone is longer than 20 characters';
  return '';
}

const emptyForm = {
  name: '',
  brand_code: '',
  email: '',
  phone: '',
  address: '',
};

/**
 * Client organizations. Embedded = the "Clients" tab of Clients & Stores.
 * onViewStores(org) / onManageLogins(org) switch to the other tabs.
 */
function Organizations({ embedded = false, onViewStores, onManageLogins }) {
  const { invalidateResource } = useAppData();
  const loadOrganizations = useCallback(() => organizationService.getAll(), []);
  const {
    data: organizations = [],
    loading,
    error: organizationsError,
    refresh: refreshOrganizations,
  } = useAppResource('organizations', loadOrganizations, []);
  const [error, setError] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const loadOutlets = useCallback(() => outletService.getAll(), []);
  const loadSchedules = useCallback(() => auditService.getAll(), []);
  const { data: outlets = [] } = useAppResource('outlets', loadOutlets, []);
  const { data: schedules = [] } = useAppResource('audits:kpi', loadSchedules, []);
  const loadUsers = useCallback(() => userService.getAll(), []);
  const { data: users = [] } = useAppResource('users', loadUsers, []);
  const clientUsers = useMemo(() => (users || []).filter((user) => user.role_name === 'client'), [users]);
  const kpis = useMemo(
    () => organizationKpis(organizations || [], outlets || [], schedules || []),
    [organizations, outlets, schedules],
  );
  const [formOpen, setFormOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const visibleError = error || organizationsError;

  const openCreateForm = () => {
    setEditingOrg(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEditForm = (org) => {
    setEditingOrg(org);
    setForm({
      name: org.name || '',
      brand_code: org.brand_code || '',
      email: org.email || '',
      phone: org.phone || '',
      address: org.address || '',
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingOrg(null);
    setForm(emptyForm);
  };

  const handleChange = (event) => {
    const { name } = event.target;
    // Brand code: letters only, upper case, at most 2.
    const value =
      name === 'brand_code'
        ? event.target.value.replace(/[^a-z]/gi, '').toUpperCase().slice(0, 2)
        : event.target.value;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      // Empty on a new organization = first two letters of its name.
      ...(form.brand_code ? { brand_code: form.brand_code } : {}),
      phone: form.phone.trim() || null,
      address: form.address.trim() || null,
    };

    try {
      if (editingOrg) {
        await organizationService.update(editingOrg.id, payload);
        toast.success('Client updated', payload.name);
      } else {
        await organizationService.create(payload);
        toast.success('Client added', payload.name);
      }
      closeForm();
      invalidateResource('outlets');
      await refreshOrganizations();
    } catch (err) {
      toast.error(editingOrg ? 'Client not updated' : 'Client not added', err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (org) => {
    const confirmed = window.confirm(`Delete organization "${org.name}"?`);
    if (!confirmed) {
      return;
    }

    setError('');

    try {
      await organizationService.delete(org.id);
      toast.success('Client deleted', org.name);
      invalidateResource('outlets');
      await refreshOrganizations();
    } catch (err) {
      toast.error('Client not deleted', err.message);
    }
  };


  return (
    <div className={embedded ? 'organizations-page organizations-page--embedded' : 'page organizations-page'}>
      <header className={embedded ? 'hub-section__toolbar' : 'page__header organizations-page__header'}>
        <div>
          {embedded ? (
            <h2>Clients</h2>
          ) : (
            <>
              <p className="page__eyebrow">Operations</p>
              <h1>Organizations</h1>
            </>
          )}
          <p className="organizations-page__subtitle">
            Client organizations (brands) with their stores, schedules and portal contacts.
          </p>
        </div>

        <div className="page-header-actions">
          <button type="button" className="bulk-open-btn" onClick={() => setBulkOpen(true)}>
            ⇪ Bulk upload
          </button>
          <button
            type="button"
            className="organizations-page__add-btn"
            onClick={openCreateForm}
          >
            + New Client
          </button>
        </div>
      </header>

      {!loading ? <StatCards items={kpis} /> : null}

      {visibleError && (
        <div className="organizations-page__alert" role="alert">
          {visibleError}
        </div>
      )}

      {bulkOpen ? (
        <BulkImportPanel
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          title="Bulk upload clients"
          noun="client"
          nounPlural="clients"
          templateName="organizations-template.csv"
          intro="Add many client organizations at once. Organizations whose name already exists are skipped. The brand code (two letters, used in schedule numbers like 2026-10-PT-001) is taken from the name when left blank."
          columns={ORG_COLUMNS}
          sampleRows={ORG_SAMPLE}
          check={checkOrganizationRow}
          upload={organizationService.bulkImport}
          onDone={refreshOrganizations}
        />
      ) : null}

      <section className="organizations-page__card">
        {loading ? (
          <PageLoader label="Loading organizations…" />
        ) : organizations.length === 0 ? (
          <div className="organizations-page__empty-state">
            <p>No organizations yet.</p>
            <button type="button" onClick={openCreateForm}>
              Create your first organization
            </button>
          </div>
        ) : (
          <ClientCards
            organizations={organizations}
            outlets={outlets || []}
            schedules={schedules || []}
            clientUsers={clientUsers}
            onEdit={openEditForm}
            onDelete={handleDelete}
            onViewStores={(org) => onViewStores?.(org)}
            onManageLogins={(org) => onManageLogins?.(org)}
          />
        )}
      </section>

      {formOpen && (
        <div className="organizations-modal">
          <div className="organizations-modal__backdrop" onClick={closeForm} />
          <div className="organizations-modal__panel" role="dialog" aria-modal="true">
            <div className="organizations-modal__header">
              <h2>{editingOrg ? 'Edit Organization' : 'New Organization'}</h2>
              <button type="button" onClick={closeForm}>
                ×
              </button>
            </div>

            <form className="organizations-form" onSubmit={handleSubmit}>
              <label>
                Name
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Acme Retail Pvt Ltd"
                />
              </label>

              <label>
                Brand code (2 letters)
                <input
                  type="text"
                  name="brand_code"
                  value={form.brand_code}
                  onChange={handleChange}
                  required={Boolean(editingOrg)}
                  pattern="[A-Z]{2}"
                  title="Two letters, e.g. PT"
                  placeholder="e.g. PT"
                  autoComplete="off"
                />
                <small className="organizations-form__hint">
                  Used in schedule numbers, e.g. 2026-10-PT-001. Changing it doesn't renumber
                  existing schedules.
                </small>
              </label>

              <label>
                Email
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="contact@acme.com"
                />
              </label>

              <label>
                Phone
                <input
                  type="text"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                />
              </label>

              <label>
                Address
                <textarea
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Street, city, state"
                />
              </label>

              <div className="organizations-form__actions">
                <button
                  type="button"
                  className="organizations-form__cancel"
                  onClick={closeForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="organizations-form__submit"
                  disabled={saving}
                >
                  {saving
                    ? 'Saving...'
                    : editingOrg
                      ? 'Update Organization'
                      : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Organizations;
